import { getMatchingPairs, getMatchingTutorials, sectionMatchesPrefix } from './matchingUtils';

const DATE_RANGE_PATTERN = /^(\d{2}-[A-Z]{3}-\d{4})\s*-\s*(\d{2}-[A-Z]{3}-\d{4})$/i;
const WEEKDAY_PATTERN = /(Mo|Tu|We|Th|Fr|Sa|Su)/gi;
const TIME_PATTERN = /\b(0[1-9]|1[0-2]):([0-5]\d)(AM|PM)\b/gi;
const TIME_VALUE_PATTERN = /^(0[1-9]|1[0-2]):([0-5]\d)(AM|PM)$/i;
const GRID_MINUTES = 30;

export function parseTime(timeString) {
    const match = typeof timeString === 'string' && timeString.match(TIME_VALUE_PATTERN);
    if (!match) return null;

    let hour = Number(match[1]);
    const minute = Number(match[2]);
    const isPm = match[3].toUpperCase() === 'PM';

    if (isPm && hour !== 12) hour += 12;
    if (!isPm && hour === 12) hour = 0;

    return { h: hour, m: minute, minutes: hour * 60 + minute };
}

function normalizeInterval(startMinutes, endMinutes) {
    const gridStartMinutes = Math.floor(startMinutes / GRID_MINUTES) * GRID_MINUTES;
    const gridEndMinutes = Math.max(
        gridStartMinutes + GRID_MINUTES,
        Math.ceil(endMinutes / GRID_MINUTES) * GRID_MINUTES
    );

    return {
        gridStartMinutes,
        gridEndMinutes,
        spanCount: (gridEndMinutes - gridStartMinutes) / GRID_MINUTES,
    };
}

function getDateRange(entry) {
    const match = entry.match(DATE_RANGE_PATTERN);
    return match ? { start: match[1].toUpperCase(), end: match[2].toUpperCase() } : null;
}

export function getSectionObjs(data, code, section) {
    return data?.[code]?.sections?.filter(item => item.section === section) || [];
}

function createTbaBox(code, section, sectionObj, isSingleton, isVirtual, dates, occurrenceIndex, reason) {
    const sourceId = sectionObj.classnum || sectionObj.room || 'meeting';
    return {
        type: 'tba',
        code,
        section,
        weekday: 'TBA',
        start: 'TBA',
        end: 'TBA',
        room: sectionObj.room || 'TBA',
        instructor: sectionObj.instructor || ['TBA'],
        isSingleton,
        isVirtual,
        dates,
        reason,
        occurrenceId: `${code}:${section}:TBA:${sourceId}:${occurrenceIndex}`,
    };
}

export function parseSectionToCourseBoxes(code, section, sectionObj, isSingleton, isVirtual) {
    if (!sectionObj) return [];

    const datetime = Array.isArray(sectionObj.datetime) && sectionObj.datetime.length > 0
        ? sectionObj.datetime
        : ['TBA'];
    const boxes = [];
    const sourceId = sectionObj.classnum || sectionObj.room || 'meeting';
    let pendingDates = null;

    datetime.forEach((rawEntry, occurrenceIndex) => {
        const entry = typeof rawEntry === 'string' ? rawEntry.trim() : '';
        const dateRange = entry && getDateRange(entry);
        if (dateRange) {
            pendingDates = dateRange;
            return;
        }

        if (!entry || entry.toUpperCase() === 'TBA') {
            boxes.push(createTbaBox(
                code, section, sectionObj, isSingleton, isVirtual, pendingDates, occurrenceIndex,
                entry ? undefined : 'Time has not been published'
            ));
            pendingDates = null;
            return;
        }

        const weekdays = entry.match(WEEKDAY_PATTERN);
        const times = entry.match(TIME_PATTERN);
        if (!weekdays || !times || times.length !== 2) {
            boxes.push(createTbaBox(
                code, section, sectionObj, isSingleton, isVirtual, pendingDates, occurrenceIndex,
                `Unrecognized schedule: ${entry}`
            ));
            pendingDates = null;
            return;
        }

        const start = parseTime(times[0]);
        const end = parseTime(times[1]);
        if (!start || !end || end.minutes <= start.minutes) {
            boxes.push(createTbaBox(
                code, section, sectionObj, isSingleton, isVirtual, pendingDates, occurrenceIndex,
                `Invalid schedule: ${entry}`
            ));
            pendingDates = null;
            return;
        }

        const interval = normalizeInterval(start.minutes, end.minutes);
        weekdays.forEach((weekday, weekdayIndex) => {
            const normalizedWeekday = weekday.slice(0, 1).toUpperCase() + weekday.slice(1).toLowerCase();
            boxes.push({
                type: 'regular',
                code,
                section,
                weekday: normalizedWeekday,
                start: times[0].toUpperCase(),
                end: times[1].toUpperCase(),
                startMinutes: start.minutes,
                endMinutes: end.minutes,
                ...interval,
                room: sectionObj.room || 'TBA',
                instructor: sectionObj.instructor || ['TBA'],
                isSingleton,
                isVirtual,
                dates: pendingDates,
                occurrenceId: `${code}:${section}:${normalizedWeekday}:${start.minutes}:${end.minutes}:${pendingDates?.start || ''}:${pendingDates?.end || ''}:${sourceId}:${occurrenceIndex}:${weekdayIndex}`,
            });
        });
        pendingDates = null;
    });

    return boxes;
}

export function getVirtualSectionBoxes(data, code, currentSection, sections, selectedSections = []) {
    const sectionType = currentSection.match(/^[A-Z]+/i)?.[0];
    if (!sectionType || !sections?.[sectionType]) return [];

    const isSingleton = sections[sectionType].length === 1;
    let matchingTutorials = [];

    if (sectionType === 'T' || sectionType === 'LA') {
        const selectedLecture = selectedSections.find(section => /^L\d/i.test(section));
        const courseData = data?.[code];
        const pairs = getMatchingPairs(courseData);
        if (selectedLecture) {
            matchingTutorials = getMatchingTutorials(courseData, selectedLecture, sections[sectionType]);
        } else if (pairs.length > 0) {
            const matchedPair = pairs.find(p => /^LA/i.test(p.tutorial) === (sectionType === 'LA') && sectionMatchesPrefix(currentSection, p.tutorial));
            if (matchedPair) {
                matchingTutorials = getMatchingTutorials(courseData, matchedPair.lecture, sections[sectionType]);
            }
        }
    }

    const alternatives = matchingTutorials.length > 0 ? matchingTutorials : sections[sectionType];

    return alternatives
        .filter(section => section !== currentSection)
        .flatMap(section => getSectionObjs(data, code, section)
            .flatMap(sectionObj => parseSectionToCourseBoxes(code, section, sectionObj, isSingleton, true)));
}

export function isValidSectionSwap(dragged, target) {
    return Boolean(
        dragged
        && target?.isVirtual
        && dragged.code === target.code
        && dragged.sectionType === target.sectionType
        && dragged.section !== target.section
    );
}

function parseDate(value) {
    const match = value?.match(/^(\d{2})-([A-Z]{3})-(\d{4})$/i);
    if (!match) return null;

    const months = {
        JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
        JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
    };
    const month = months[match[2].toUpperCase()];
    return month === undefined ? null : Date.UTC(Number(match[3]), month, Number(match[1]));
}

function datesOverlap(first, second) {
    if (!first?.start || !first?.end || !second?.start || !second?.end) return true;

    const firstStart = parseDate(first.start);
    const firstEnd = parseDate(first.end);
    const secondStart = parseDate(second.start);
    const secondEnd = parseDate(second.end);
    if (firstStart === null || firstEnd === null || secondStart === null || secondEnd === null) return true;

    return firstStart <= secondEnd && secondStart <= firstEnd;
}

export function meetingsOverlap(first, second) {
    return first.weekday === second.weekday
        && first.startMinutes < second.endMinutes
        && second.startMinutes < first.endMinutes
        && datesOverlap(first.dates, second.dates);
}

export function buildMeetingLanes(boxes) {
    const lanes = [];
    const sortedBoxes = [...boxes].sort((first, second) => (
        first.gridStartMinutes - second.gridStartMinutes
        || second.gridEndMinutes - first.gridEndMinutes
        || first.occurrenceId.localeCompare(second.occurrenceId)
    ));

    sortedBoxes.forEach(box => {
        const lane = lanes.find(candidate => candidate.every(existing => (
            existing.gridEndMinutes <= box.gridStartMinutes
            || box.gridEndMinutes <= existing.gridStartMinutes
        )));
        if (lane) {
            lane.push(box);
        } else {
            lanes.push([box]);
        }
    });

    return lanes;
}

export function getGridBounds(boxes) {
    const regularBoxes = boxes.filter(box => box.type === 'regular');
    const earliest = regularBoxes.length
        ? Math.min(...regularBoxes.map(box => box.gridStartMinutes))
        : 9 * 60;
    const latest = regularBoxes.length
        ? Math.max(...regularBoxes.map(box => box.gridEndMinutes))
        : 23 * 60;

    return {
        startMinutes: Math.min(9 * 60, earliest),
        endMinutes: Math.max(23 * 60, latest),
    };
}
